import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { adminAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './Manage.css';

const ManageEducation = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ institution: '', degree: '', fieldOfStudy: '', startDate: '', endDate: '', isCurrent: false, description: '', grade: '' });

  const loadData = async () => {
    try {
      const res = await adminAPI.getEducation();
      if (res.success) setItems(res.data);
    } catch (err) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ institution: '', degree: '', fieldOfStudy: '', startDate: '', endDate: '', isCurrent: false, description: '', grade: '' });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      institution: item.institution, degree: item.degree,
      fieldOfStudy: item.fieldOfStudy || '',
      startDate: item.startDate?.split('T')[0] || '', endDate: item.endDate?.split('T')[0] || '',
      isCurrent: item.isCurrent, description: item.description || '', grade: item.grade || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await adminAPI.updateEducation(editing.id, form); toast.success('Updated'); }
      else { await adminAPI.createEducation(form); toast.success('Created'); }
      setShowModal(false); loadData();
    } catch (err) { toast.error('Operation failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this education entry?')) return;
    try { await adminAPI.deleteEducation(id); toast.success('Deleted'); loadData(); }
    catch (err) { toast.error('Failed to delete'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="manage-page">
      <Helmet><title>Manage Education | Portfolio CMS</title></Helmet>
      <div className="manage-header">
        <h2>Education Management</h2>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus /> New Education</button>
      </div>
      <div className="manage-table">
        <table>
          <thead><tr><th>Institution</th><th>Degree</th><th>Field</th><th>Duration</th><th>Grade</th><th>Actions</th></tr></thead>
          <tbody>
            {items.map(e => (
              <tr key={e.id}>
                <td><strong>{e.institution}</strong></td>
                <td>{e.degree}</td>
                <td>{e.fieldOfStudy}</td>
                <td>{formatDate(e.startDate)} - {e.isCurrent ? 'Present' : formatDate(e.endDate)}</td>
                <td>{e.grade || '-'}</td>
                <td><div className="action-btns">
                  <button className="btn-icon edit" onClick={() => openEdit(e)}><FiEdit2 /></button>
                  <button className="btn-icon delete" onClick={() => handleDelete(e.id)}><FiTrash2 /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editing ? 'Edit Education' : 'New Education'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group"><label>Institution</label><input value={form.institution} onChange={e => setForm({...form, institution: e.target.value})} required /></div>
                <div className="form-group"><label>Degree</label><input value={form.degree} onChange={e => setForm({...form, degree: e.target.value})} required /></div>
              </div>
              <div className="form-group"><label>Field of Study</label><input value={form.fieldOfStudy} onChange={e => setForm({...form, fieldOfStudy: e.target.value})} /></div>
              <div className="form-row">
                <div className="form-group"><label>Start Date</label><input type="date" value={form.startDate} onChange={e => setForm({...form, startDate: e.target.value})} required /></div>
                <div className="form-group"><label>End Date</label><input type="date" value={form.endDate} onChange={e => setForm({...form, endDate: e.target.value})} disabled={form.isCurrent} /></div>
              </div>
              <div className="form-group"><label><input type="checkbox" checked={form.isCurrent} onChange={e => setForm({...form, isCurrent: e.target.checked})} /> Currently enrolled</label></div>
              <div className="form-group"><label>Grade</label><input value={form.grade} onChange={e => setForm({...form, grade: e.target.value})} placeholder="e.g., 8.5 CGPA, First Class" /></div>
              <div className="form-group"><label>Description</label><textarea rows="3" value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageEducation;
